/**
 * ANOVA 結果をダッシュボードの ResultCache 形式に変換するアダプター
 * Phase 2: 既存の oneWayANOVA / twoWayANOVA / repeatedMeasuresOneWayANOVA を呼び出し、
 * 右ペイン表示用に変換する。
 * 1要因・反復測定で3群以上の場合は群間有意差のため Tukey 事後検定を必ず実行する。
 */

import {
  oneWayANOVA,
  twoWayANOVA,
  repeatedMeasuresOneWayANOVA,
  lmmOneWayANOVA,
  mixedDesignTwoWayANOVA,
  lmmTwoWayANOVA,
  fullRepeatedTwoWayANOVA,
  type ANOVAResult,
  type TwoWayANOVAResult,
} from '../statistics/anova';
import { kruskalWallisTest, type KruskalWallisResult } from '../statistics/nonparametric';
import { computeTukeySignificancePairs, computeTukeyAllPairs } from '../statistics/tukey';
import {
  computeBonferroniAllPairs,
  computeBonferroniSignificancePairs,
} from '../statistics/anovaPairwiseBonferroni';
import {
  computeDunnettAllPairs,
  computeDunnettSignificancePairs,
  computeHolmAllPairs,
  computeHolmSignificancePairs,
  computeScheffeAllPairs,
  computeScheffeSignificancePairs,
  resolveControlGroupName,
} from '../statistics/anovaPairwiseExtended';
import type { DataRow } from '../../stores/systems/keisuke/dashboardStore';
import type { AnalysisOptions, ResultCache, VariableMapping } from '../../stores/systems/keisuke/dashboardStore';
import { parseFiniteNumberFromCell } from '../keisukeGridData';
import { kt, formatAdapterPValue, KeisukeAdapterError } from '../keisukeAdapterI18n';

type OneWayPostHocMethod =
  | 'tukey'
  | 'bonferroni'
  | 'holm'
  | 'scheffe'
  | 'dunnett'
  | undefined;

function resolvePostHocLabel(
  postHoc: OneWayPostHocMethod | string | undefined,
  twoWay: boolean
): string {
  if (postHoc === 'bonferroni') {
    return kt(twoWay ? 'statsDetail.postHoc.bonferroniTwoWay' : 'statsDetail.postHoc.bonferroniOneWay');
  }
  if (postHoc === 'tukey') {
    return kt(twoWay ? 'statsDetail.postHoc.tukeyTwoWay' : 'statsDetail.postHoc.tukeyOneWay');
  }
  if (postHoc === 'holm') {
    return kt(twoWay ? 'statsDetail.postHoc.holmTwoWay' : 'statsDetail.postHoc.holmOneWay');
  }
  if (postHoc === 'scheffe') {
    return kt(twoWay ? 'statsDetail.postHoc.scheffeTwoWay' : 'statsDetail.postHoc.scheffeOneWay');
  }
  if (postHoc === 'dunnett') {
    return kt(twoWay ? 'statsDetail.postHoc.dunnettTwoWay' : 'statsDetail.postHoc.dunnettOneWay');
  }
  return kt('statsDetail.postHoc.none');
}

function resolveOneWayDesignKey(
  isRepeatedMeasures: boolean,
  repeatedMethod: 'repeated' | 'lmm'
): string {
  if (repeatedMethod === 'lmm') return 'statsDetail.anova.designLmm';
  if (isRepeatedMeasures) return 'statsDetail.anova.designRepeated';
  return 'statsDetail.anova.designOneWay';
}

function resolveTwoWayDesignKey(design: string): string {
  if (design === 'lmm') return 'statsDetail.anova.twoWayLmm';
  if (design === 'mixed') return 'statsDetail.anova.twoWayMixed';
  if (design === 'full-repeated') return 'statsDetail.anova.twoWayFullRepeated';
  return 'statsDetail.anova.twoWayIndependent';
}

function formatPValue(p: number): string {
  if (p < 0.001) return 'p < 0.001';
  if (p < 0.01) return `p = ${p.toFixed(3)}`;
  return `p = ${p.toFixed(2)}`;
}

/**
 * 事後検定: UI の postHoc と群数から実際に使う手続きを決める。
 * - 明示値（tukey / bonferroni / …）はそのまま
 * - `''` = 明示的になし
 * - `undefined` = 未設定。3群以上なら Tukey（従来どおり・群間表示のため）
 * 左ペインの選択表示もこの関数に合わせること（「なし」表示なのに Tukey 結果、を防ぐ）
 */
export function resolveOneWayPostHoc(
  k: number,
  raw: string | undefined
): OneWayPostHocMethod {
  if (raw === 'bonferroni') return 'bonferroni';
  if (raw === 'tukey') return 'tukey';
  if (raw === 'holm') return 'holm';
  if (raw === 'scheffe') return 'scheffe';
  if (raw === 'dunnett') return 'dunnett';
  if (raw === '') return undefined;
  return k >= 3 ? 'tukey' : undefined;
}

function computeOneWayPostHocAllPairs(
  result: ANOVAResult,
  postHoc: OneWayPostHocMethod,
  controlGroupName?: string
) {
  if (!postHoc) return undefined;
  if (postHoc === 'tukey') return computeTukeyAllPairs(result);
  if (postHoc === 'bonferroni') return computeBonferroniAllPairs(result);
  if (postHoc === 'holm') return computeHolmAllPairs(result);
  if (postHoc === 'scheffe') return computeScheffeAllPairs(result);
  if (postHoc === 'dunnett') {
    const control = resolveControlGroupName(result.groups, controlGroupName);
    return computeDunnettAllPairs(result, control);
  }
  return undefined;
}

function computeOneWayPostHocSignificancePairs(
  result: ANOVAResult,
  postHoc: OneWayPostHocMethod,
  controlGroupName?: string
) {
  if (!postHoc) return undefined;
  if (postHoc === 'tukey') return computeTukeySignificancePairs(result);
  if (postHoc === 'bonferroni') return computeBonferroniSignificancePairs(result);
  if (postHoc === 'holm') return computeHolmSignificancePairs(result);
  if (postHoc === 'scheffe') return computeScheffeSignificancePairs(result);
  if (postHoc === 'dunnett') {
    const control = resolveControlGroupName(result.groups, controlGroupName);
    return computeDunnettSignificancePairs(result, control);
  }
  return undefined;
}

function postHocLabelForPaper(postHoc: OneWayPostHocMethod): string {
  if (postHoc === 'bonferroni') return 'Bonferroni correction';
  if (postHoc === 'holm') return 'Holm correction';
  if (postHoc === 'scheffe') return "Scheffé's method";
  if (postHoc === 'dunnett') return "Dunnett's test";
  return "Tukey's HSD";
}

/** レガシー計助 ANOVAView と同様の論文用 Methods + Results 英文（一元配置） */
function buildOneWayPaperText(
  result: ANOVAResult,
  postHoc: OneWayPostHocMethod,
  significancePairs: Array<{ leftIndex: number; rightIndex: number; pValue: number }> | undefined,
  isRepeatedMeasures: boolean,
  repeatedMethod: 'repeated' | 'lmm' = 'repeated',
  controlGroupName?: string
): string {
  const totalN = result.groups.reduce((acc, g) => acc + g.n, 0);
  const k = result.groups.length;
  const groupText = k === 3 ? 'three' : String(k);
  const designSentence =
    repeatedMethod === 'lmm'
      ? 'A linear mixed-effects model with a random intercept for subjects was fitted. Not all subjects had observations in all groups.'
      : isRepeatedMeasures
        ? 'A repeated-measures one-way ANOVA was performed with subjects treated as a random factor.'
        : 'One-way analysis of variance (ANOVA) was performed.';
  const lmmMethodsNote =
    repeatedMethod === 'lmm'
      ? ' Degrees of freedom were estimated using an approximation (e.g., Satterthwaite).'
      : '';
  const postHocMethodLabel = postHoc ? postHocLabelForPaper(postHoc) : '';
  const postHocArticle = postHoc === 'bonferroni' || postHoc === 'holm' ? 'the ' : '';
  const controlName = postHoc === 'dunnett' ? resolveControlGroupName(result.groups, controlGroupName) : '';
  const postHocSentence =
    postHoc === 'tukey'
      ? "When a significant main effect was detected, post hoc pairwise comparisons were conducted using Tukey's HSD."
      : postHoc === 'bonferroni'
        ? 'When a significant main effect was detected, post hoc pairwise comparisons were conducted using Bonferroni-adjusted pairwise t-tests with the ANOVA mean square error.'
        : postHoc === 'holm'
          ? 'When a significant main effect was detected, post hoc pairwise comparisons were conducted using the Holm step-down procedure.'
          : postHoc === 'scheffe'
            ? "When a significant main effect was detected, post hoc pairwise comparisons were conducted using Scheffé's method."
            : postHoc === 'dunnett'
              ? `When a significant main effect was detected, post hoc comparisons were conducted using ${postHocMethodLabel}, with the ${controlName} group as the reference.`
              : '';
  const methods =
    `${designSentence} The analysis compared ${groupText} groups (k = ${k}, N = ${totalN}) at a significance level of α = 0.05.${lmmMethodsNote} ${postHocSentence}`.trim();

  const groupStats = result.groups
    .map((g) => `${g.name}, ${g.mean.toFixed(2)} ± ${g.stdDev.toFixed(2)} (n = ${g.n})`)
    .join('; ');
  const mainP = formatPValue(result.pValue);
  const modelLabel =
    repeatedMethod === 'lmm'
      ? 'A linear mixed-effects model'
      : isRepeatedMeasures
        ? 'Repeated-measures one-way ANOVA'
        : 'One-way ANOVA';
  const effectPhrase =
    repeatedMethod === 'lmm'
      ? result.pValue < 0.05
        ? 'a significant fixed effect of group'
        : 'no significant fixed effect of group'
      : result.pValue < 0.05
        ? 'a significant main effect'
        : 'no significant main effect';
  const mainSentence = `${modelLabel} showed ${effectPhrase} (F(${result.dfBetween}, ${result.dfWithin}) = ${result.f.toFixed(2)}, ${mainP}).`;

  const postHocLabel =
    postHoc === 'bonferroni'
      ? 'Bonferroni-adjusted pairwise tests'
      : postHoc === 'holm'
        ? `${postHocArticle}Holm correction`
        : postHoc === 'scheffe'
          ? `${postHocArticle}Scheffé's method`
          : postHoc === 'dunnett'
            ? `${postHocLabelForPaper('dunnett')} (reference: ${controlName})`
            : "Tukey's HSD";

  let postHocResult = '';
  if (significancePairs && significancePairs.length > 0 && result.pValue < 0.05) {
    const significant = significancePairs.map(
      (p) => `${result.groups[p.leftIndex].name} vs ${result.groups[p.rightIndex].name}`
    );
    const allPairs = (k * (k - 1)) / 2;
    const nonsignificant =
      allPairs > significant.length
        ? significancePairs.length === allPairs
          ? []
          : ['other pairs'] // legacy says "whereas X were not significant"
        : [];
    if (significant.length === allPairs) {
      postHocResult = ` Post hoc analysis using ${postHocLabel} revealed that all pairwise comparisons were statistically significant.`;
    } else if (significant.length === 0) {
      postHocResult = ` Post hoc analysis using ${postHocLabel} revealed that none of the pairwise comparisons were statistically significant.`;
    } else {
      postHocResult = ` Post hoc analysis using ${postHocLabel} revealed that ${significant.join(', ')} were statistically significant${nonsignificant.length ? ', whereas others were not significant' : ''}.`;
    }
  }

  const results = `${groupStats}. ${mainSentence}${postHocResult}`;
  return `Methods\n\n${methods}\n\nResults\n\n${results}`;
}

/** 論文用 Methods + Results（Kruskal–Wallis；事後検定は実施しない） */
function buildKruskalPaperText(kw: KruskalWallisResult): string {
  const totalN = kw.groups.reduce((acc, g) => acc + g.n, 0);
  const k = kw.groups.length;
  const groupText = k === 3 ? 'three' : String(k);
  const methods =
    `The Kruskal–Wallis test was performed as a nonparametric alternative to one-way ANOVA. ` +
    `The analysis compared ${groupText} independent groups (k = ${k}, N = ${totalN}) at a significance level of α = 0.05. ` +
    `Pairwise post hoc comparisons were not performed; follow-up tests (e.g., Dunn's test) are required for specific group contrasts.`;

  const groupStats = kw.groups
    .map((g) => `${g.name}, median = ${g.median.toFixed(2)} (n = ${g.n})`)
    .join('; ');
  const mainP = formatPValue(kw.pValue);
  const mainSentence = `The Kruskal–Wallis test showed ${
    kw.pValue < 0.05 ? 'a significant difference among groups' : 'no significant difference among groups'
  } (H(${kw.df}) = ${kw.H.toFixed(2)}, ${mainP}).`;

  return `Methods\n\n${methods}\n\nResults\n\n${groupStats}. ${mainSentence}`;
}

function kruskalWallisResultToCache(kw: KruskalWallisResult): ResultCache {
  const stats: Record<string, unknown> = {
    H: Math.round(kw.H * 1000) / 1000,
    'df': kw.df,
    pValue: kw.pValue,
    method: 'kruskal-wallis',
  };
  kw.groups.forEach((g) => {
    stats[`${g.name} (median)`] = Math.round(g.median * 1000) / 1000;
    stats[`${g.name} (n)`] = g.n;
  });

  const paperText = buildKruskalPaperText(kw);
  const statsDetailText = [
    kt('statsDetail.overviewTitle'),
    kt('statsDetail.anova.kruskalIntro'),
    kt('statsDetail.anova.kruskalStatsLine', {
      h: kw.H.toFixed(4),
      df: kw.df,
      p: formatAdapterPValue(kw.pValue),
    }),
    kt('statsDetail.anova.kruskalPostHocNote'),
    kt('statsDetail.formulasRef'),
  ].join('\n');

  return {
    stats,
    paperText,
    statsDetailText,
    significancePairs: undefined,
    anovaPairwise: undefined,
    rawResult: { kruskalWallis: kw },
    updatedAt: Date.now(),
  };
}

function oneWayResultToCache(
  result: ANOVAResult,
  postHoc: OneWayPostHocMethod,
  isRepeatedMeasures = false,
  repeatedMethod: 'repeated' | 'lmm' = 'repeated',
  controlGroupName?: string
): ResultCache {
  const stats: Record<string, unknown> = {
    F: Math.round(result.f * 1000) / 1000,
    'df (between)': result.dfBetween,
    'df (within)': result.dfWithin,
    pValue: result.pValue,
    'SS between': Math.round(result.ssBetween * 1000) / 1000,
    'SS within': Math.round(result.ssWithin * 1000) / 1000,
    method: repeatedMethod === 'lmm' ? 'lmm' : isRepeatedMeasures ? 'repeated-measures' : 'one-way',
  };
  if (result.varianceComponents) {
    stats['σ² random intercept'] = result.varianceComponents.randomIntercept;
    stats['σ² residual'] = result.varianceComponents.residual;
  }
  result.groups.forEach((g) => {
    stats[`${g.name} (mean)`] = Math.round(g.mean * 1000) / 1000;
    stats[`${g.name} (n)`] = g.n;
  });

  const significancePairs = computeOneWayPostHocSignificancePairs(result, postHoc, controlGroupName);
  const allPairs = computeOneWayPostHocAllPairs(result, postHoc, controlGroupName);
  const anovaPairwise =
    allPairs?.map((p) => ({
      groupA: result.groups[p.leftIndex].name,
      groupB: result.groups[p.rightIndex].name,
      pValue: p.pValue,
      significant: p.significant,
    }));

  const paperText = buildOneWayPaperText(
    result,
    postHoc,
    significancePairs ?? undefined,
    isRepeatedMeasures,
    repeatedMethod,
    controlGroupName
  );

  const postHocLabel = resolvePostHocLabel(postHoc, false);
  const designKey = resolveOneWayDesignKey(isRepeatedMeasures, repeatedMethod);
  const pStr = formatAdapterPValue(result.pValue);
  const statsDetailText = [
    kt('statsDetail.overviewTitle'),
    kt('statsDetail.anova.omnibusLine', {
      design: kt(designKey),
      dfBetween: result.dfBetween,
      dfWithin: result.dfWithin,
      f: result.f.toFixed(4),
      p: pStr,
      lmmNote: repeatedMethod === 'lmm' ? kt('statsDetail.anova.lmmNote') : '',
    }),
    kt('statsDetail.anova.postHocLine', { postHoc: postHocLabel }),
    kt('statsDetail.formulasRef'),
  ].join('\n');

  return {
    stats,
    paperText,
    statsDetailText,
    significancePairs,
    anovaPairwise,
    rawResult: result,
    updatedAt: Date.now(),
  };
}

/** レガシー計助 ANOVAView と同様の論文用 Methods + Results 英文（二元配置） */
function buildTwoWayPaperText(result: TwoWayANOVAResult, postHoc?: string): string {
  const aLevels = result.factorA.df + 1;
  const bLevels = result.factorB.df + 1;
  const design = result.design ?? 'independent';
  let methodsIntro: string;
  if (design === 'mixed') {
    methodsIntro = `Mixed-design two-way ANOVA was performed with Factor A (between-subject, ${aLevels} levels) and Factor B (within-subject, ${bLevels} levels) as fixed effects.`;
  } else if (design === 'full-repeated') {
    methodsIntro = `Repeated-measures two-way ANOVA was performed with Factor A (${aLevels} levels) and Factor B (${bLevels} levels) as within-subject factors.`;
  } else if (design === 'lmm') {
    methodsIntro =
      `A linear mixed-effects model with a random intercept for subjects was fitted for Factor A (${aLevels} levels) and Factor B (${bLevels} levels) and their interaction. ` +
      'Fixed effects were tested using an F-test with approximate degrees of freedom (Satterthwaite approximation, etc.). Sum-of-squares decomposition does not apply to LMM.';
  } else {
    methodsIntro = `Two-way ANOVA was performed with Factor A (${aLevels} levels) and Factor B (${bLevels} levels) as fixed effects, assuming independent observations.`;
  }
  const methodsOmnibus =
    design === 'lmm'
      ? ' Omnibus F-tests evaluate overall main effects of Factor A and Factor B and the A × B interaction; they do not identify which specific cell means or pairwise contrasts differ.'
      : ' Omnibus F-tests were used to evaluate overall main effects of Factor A and Factor B and the A × B interaction; these tests indicate whether factors contribute variance on average but do not identify which specific cell means or pairwise contrasts differ.';
  let methodsPostHoc = '';
  if (postHoc === 'tukey') {
    methodsPostHoc =
      " Post hoc pairwise comparisons were conducted using Tukey's honestly significant difference (HSD) test with the within-cells mean square as the error term, as specified in the analysis settings." +
      ' Pairwise comparisons among Factor A levels used marginal means aggregated across Factor B (a simplified follow-up to the two-way design; cell-specific contrasts may require simple-effects tests).';
  } else if (postHoc === 'bonferroni') {
    methodsPostHoc =
      ' Post hoc pairwise comparisons were conducted using Bonferroni-adjusted tests, as specified in the analysis settings.' +
      ' Pairwise comparisons among Factor A levels used marginal means aggregated across Factor B (a simplified follow-up to the two-way design).';
  } else if (postHoc === 'holm') {
    methodsPostHoc =
      ' Post hoc pairwise comparisons were conducted using the Holm step-down procedure, as specified in the analysis settings.' +
      ' Pairwise comparisons among Factor A levels used marginal means aggregated across Factor B (a simplified follow-up to the two-way design).';
  } else if (postHoc === 'scheffe') {
    methodsPostHoc =
      " Post hoc pairwise comparisons were conducted using Scheffé's method, as specified in the analysis settings." +
      ' Pairwise comparisons among Factor A levels used marginal means aggregated across Factor B (a simplified follow-up to the two-way design).';
  } else if (postHoc === 'dunnett') {
    methodsPostHoc =
      " Post hoc comparisons were conducted using Dunnett's test against a designated control level of Factor A, as specified in the analysis settings." +
      ' Comparisons used marginal means aggregated across Factor B (a simplified follow-up to the two-way design).';
  } else {
    methodsPostHoc =
      ' No post hoc pairwise procedure was selected; claims about differences between particular groups or cells require follow-up tests (e.g., Tukey HSD, simple effects, or planned contrasts) and should not be inferred from omnibus F-tests alone.';
  }
  const methods = `${methodsIntro}${methodsOmnibus}${methodsPostHoc}`;

  const dfErrorA = result.factorA.errorDf ?? result.within.df;
  const dfErrorB = result.factorB.errorDf ?? result.within.df;
  const dfErrorAB = result.interaction.errorDf ?? result.within.df;
  const formatPForReport = (p: number) =>
    p <= 0 || !Number.isFinite(p) ? 'p < 1×10⁻¹⁵' : `p = ${formatPValue(p)}`;
  const pA = formatPForReport(result.factorA.pValue);
  const pB = formatPForReport(result.factorB.pValue);
  const pAB = formatPForReport(result.interaction.pValue);
  const resultsLabel =
    design === 'mixed'
      ? 'Mixed-design two-way ANOVA'
      : design === 'full-repeated'
        ? 'Repeated-measures two-way ANOVA'
        : design === 'lmm'
          ? 'Linear mixed-effects model'
          : 'Two-way ANOVA';
  const resultsBody = `${resultsLabel} showed a main effect of Factor A (F(${result.factorA.df}, ${dfErrorA}) = ${result.factorA.f.toFixed(2)}, ${pA}), a main effect of Factor B (F(${result.factorB.df}, ${dfErrorB}) = ${result.factorB.f.toFixed(2)}, ${pB}), and an interaction effect of Factor A × Factor B (F(${result.interaction.df}, ${dfErrorAB}) = ${result.interaction.f.toFixed(2)}, ${pAB}).`;
  const resultsNote =
    ' The p-values above refer to omnibus tests; pairwise or cell-specific conclusions require the corresponding post hoc or simple-effects results.';

  return `Methods\n\n${methods}\n\nResults\n\n${resultsBody}${resultsNote}`;
}

/**
 * 2要因ANOVA 時の棒グラフは因子A列のみで集計するため、Tukey は「因子Aの水準間（周辺平均）」を
 * 合成した一元配置として扱い、二元配置の MSW・df でペア比較する（設計上の簡略化）。
 * 各ペアの p は一元配置と同様 R `TukeyHSD.aov` と同型（Tukey–Kramer／ptukey）。
 */
function buildTwoWayFactorASyntheticAnova(
  twoWay: TwoWayANOVAResult,
  gridData: DataRow[],
  factorACol: string,
  valueCol: string
): ANOVAResult | null {
  const map = new Map<string, number[]>();
  for (const row of gridData) {
    const g = String(row[factorACol] ?? '').trim();
    const num = parseFiniteNumberFromCell(row[valueCol]);
    if (num === null || !g) continue;
    if (!map.has(g)) map.set(g, []);
    map.get(g)!.push(num);
  }
  const groupList = Array.from(map.entries()).map(([name, values]) => {
    const mean = values.reduce((s, x) => s + x, 0) / values.length;
    const variance =
      values.length > 1
        ? values.reduce((s, x) => s + (x - mean) ** 2, 0) / (values.length - 1)
        : 0;
    return { name, mean, n: values.length, stdDev: Math.sqrt(variance) };
  });
  if (groupList.length < 2) return null;
  return {
    f: twoWay.factorA.f,
    pValue: twoWay.factorA.pValue,
    dfBetween: twoWay.factorA.df,
    dfWithin: twoWay.within.df,
    ssBetween: twoWay.factorA.ss,
    ssWithin: twoWay.within.ss,
    msBetween: twoWay.factorA.ms,
    msWithin: twoWay.within.ms,
    groups: groupList,
  };
}

function twoWayResultToCache(
  result: TwoWayANOVAResult,
  opts?: {
    significancePairs?: Array<{ leftIndex: number; rightIndex: number; pValue: number }>;
    postHoc?: string;
    anovaPairwise?: Array<{ groupA: string; groupB: string; pValue: number; significant: boolean }>;
  }
): ResultCache {
  const stats: Record<string, unknown> = {
    'Factor A F': Math.round(result.factorA.f * 1000) / 1000,
    'Factor A p': result.factorA.pValue,
    'Factor B F': Math.round(result.factorB.f * 1000) / 1000,
    'Factor B p': result.factorB.pValue,
    'Interaction F': Math.round(result.interaction.f * 1000) / 1000,
    'Interaction p': result.interaction.pValue,
  };

  const paperText = buildTwoWayPaperText(result, opts?.postHoc);

  const ph = opts?.postHoc ?? '';
  const postHocLabel = resolvePostHocLabel(
    ph === 'tukey' ? 'tukey' : ph === 'bonferroni' ? 'bonferroni' : undefined,
    true
  );
  const design = result.design ?? 'independent';
  const designKey = resolveTwoWayDesignKey(design);
  const fmtP = (p: number) => formatAdapterPValue(p);
  const factorLine = (
    factorLabelKey: string,
    dfNum: number,
    dfDen: number,
    f: number,
    p: number
  ) =>
    kt('statsDetail.anova.factorLine', {
      factorLabel: kt(factorLabelKey),
      dfNum,
      dfDen,
      f: f.toFixed(4),
      p: fmtP(p),
    });
  const statsDetailText = [
    kt('statsDetail.overviewTitle'),
    `${kt(designKey)}。${kt('statsDetail.anova.twoWayIntro')}`,
    factorLine(
      'statsDetail.anova.factorA',
      result.factorA.df,
      result.factorA.errorDf ?? result.within.df,
      result.factorA.f,
      result.factorA.pValue
    ),
    factorLine(
      'statsDetail.anova.factorB',
      result.factorB.df,
      result.factorB.errorDf ?? result.within.df,
      result.factorB.f,
      result.factorB.pValue
    ),
    factorLine(
      'statsDetail.anova.interaction',
      result.interaction.df,
      result.interaction.errorDf ?? result.within.df,
      result.interaction.f,
      result.interaction.pValue
    ),
    design === 'full-repeated' ? kt('statsDetail.anova.fullRepeatedWarning') : null,
    kt('statsDetail.anova.twoWayPostHocLine', { postHoc: postHocLabel }),
    kt('statsDetail.formulasRef'),
  ]
    .filter((line): line is string => line != null)
    .join('\n');

  return {
    stats,
    paperText,
    statsDetailText,
    significancePairs: opts?.significancePairs,
    anovaPairwise: opts?.anovaPairwise,
    rawResult: result,
    updatedAt: Date.now(),
  };
}

/**
 * gridData と analysisOptions から ANOVA を実行し、ResultCache を返す。
 */
function isTwoWayPostHocMethod(postHoc: string): postHoc is NonNullable<OneWayPostHocMethod> {
  return (
    postHoc === 'tukey' ||
    postHoc === 'bonferroni' ||
    postHoc === 'holm' ||
    postHoc === 'scheffe' ||
    postHoc === 'dunnett'
  );
}

function mapAnovaPairsToGroupRows(
  result: ANOVAResult,
  pairs: Array<{ leftIndex: number; rightIndex: number; pValue: number; significant: boolean }>
) {
  return pairs.map((p) => ({
    groupA: result.groups[p.leftIndex].name,
    groupB: result.groups[p.rightIndex].name,
    pValue: p.pValue,
    significant: p.significant,
  }));
}

export function runAnovaAdapter(
  gridData: DataRow[],
  options: AnalysisOptions
): ResultCache {
  const anovaType = (options.anovaType as string) ?? 'oneWay';
  const mapping: VariableMapping = options.mapping ?? {};

  if (gridData.length === 0) {
    throw new KeisukeAdapterError('dataRequired');
  }

  if (anovaType === 'oneWay') {
    const groupCol = mapping.groupColumn ?? mapping.factorAColumn;
    const valueCol = mapping.valueColumn;
    const oneWayMethod = options.anovaOneWayMethod ?? 'anova';
    if (!groupCol || !valueCol) {
      throw new KeisukeAdapterError('groupAndValueColumnsRequired');
    }
    const data = gridData
      .map((row) => {
        const num = parseFiniteNumberFromCell(row[valueCol]);
        if (num === null) return null;
        return { group: String(row[groupCol] ?? '').trim(), value: num };
      })
      .filter((r): r is { group: string; value: number } => r !== null && r.group !== '');
    if (data.length === 0) throw new KeisukeAdapterError('invalidNumericData');

    if (oneWayMethod === 'kruskal-wallis') {
      const groupMap = new Map<string, number[]>();
      for (const row of data) {
        if (!groupMap.has(row.group)) groupMap.set(row.group, []);
        groupMap.get(row.group)!.push(row.value);
      }
      const kw = kruskalWallisTest(
        Array.from(groupMap.entries()).map(([name, values]) => ({ name, values }))
      );
      return kruskalWallisResultToCache(kw);
    }

    const result = oneWayANOVA(data);
    const effectivePostHoc = resolveOneWayPostHoc(result.groups.length, options.postHoc as string | undefined);
    return oneWayResultToCache(
      result,
      effectivePostHoc,
      false,
      'repeated',
      options.anovaControlGroup as string | undefined
    );
  }

  if (anovaType === 'twoWay') {
    const factorA = mapping.factorAColumn ?? mapping.groupColumn;
    const factorB = mapping.factorBColumn;
    const valueCol = mapping.valueColumn;
    const subjectCol = mapping.subjectColumn;
    const twoWayMethod = options.twoWayMethod ?? 'independent';
    if (!factorA || !factorB || !valueCol) {
      throw new KeisukeAdapterError('factorColumnsRequired');
    }
    if (
      (twoWayMethod === 'mixed' || twoWayMethod === 'lmm' || twoWayMethod === 'full-repeated') &&
      !subjectCol
    ) {
      throw new KeisukeAdapterError('subjectIdRequiredForMixed');
    }
    if (twoWayMethod === 'full-repeated' && !options.twoWayFullRepeatedConfirm) {
      throw new KeisukeAdapterError('fullRepeatedConfirmRequired');
    }
    const data = gridData
      .map((row) => {
        const num = parseFiniteNumberFromCell(row[valueCol]);
        if (num === null) return null;
        const fa = String(row[factorA] ?? '').trim();
        const fb = String(row[factorB] ?? '').trim();
        if (!fa || !fb) return null;
        if (
          twoWayMethod === 'mixed' ||
          twoWayMethod === 'lmm' ||
          twoWayMethod === 'full-repeated'
        ) {
          const subject = String(row[subjectCol!] ?? '').trim();
          if (!subject) return null;
          return { factorA: fa, factorB: fb, subject, value: num };
        }
        return { factorA: fa, factorB: fb, value: num };
      })
      .filter((r) => r !== null) as Array<
        | { factorA: string; factorB: string; value: number }
        | { factorA: string; factorB: string; subject: string; value: number }
      >;
    if (data.length === 0) throw new KeisukeAdapterError('invalidNumericData');

    let result: TwoWayANOVAResult;
    if (twoWayMethod === 'mixed') {
      result = mixedDesignTwoWayANOVA(
        data as Array<{ factorA: string; factorB: string; subject: string; value: number }>
      );
    } else if (twoWayMethod === 'lmm') {
      result = lmmTwoWayANOVA(
        data as Array<{ factorA: string; factorB: string; subject: string; value: number }>
      );
    } else if (twoWayMethod === 'full-repeated') {
      result = fullRepeatedTwoWayANOVA(
        data as Array<{ factorA: string; factorB: string; subject: string; value: number }>
      );
    } else {
      result = twoWayANOVA(
        data as Array<{ factorA: string; factorB: string; value: number }>
      );
    }
    const postHoc = (options.postHoc as string) ?? '';
    const controlGroup = options.anovaControlGroup as string | undefined;
    const synthetic = isTwoWayPostHocMethod(postHoc)
      ? buildTwoWayFactorASyntheticAnova(result, gridData, factorA, valueCol)
      : null;
    const resolvedPostHoc = isTwoWayPostHocMethod(postHoc) ? postHoc : undefined;
    const pairs =
      synthetic && resolvedPostHoc
        ? computeOneWayPostHocSignificancePairs(synthetic, resolvedPostHoc, controlGroup)
        : undefined;
    const allPairs =
      synthetic && resolvedPostHoc
        ? computeOneWayPostHocAllPairs(synthetic, resolvedPostHoc, controlGroup)
        : undefined;
    const anovaPairwiseTwoWay =
      synthetic && allPairs ? mapAnovaPairsToGroupRows(synthetic, allPairs) : undefined;
    const cache = twoWayResultToCache(result, {
      significancePairs: pairs,
      postHoc,
      anovaPairwise: anovaPairwiseTwoWay,
    });
    if (twoWayMethod !== 'independent') {
      cache.stats!['twoWayMethod'] = twoWayMethod;
    }
    return cache;
  }

  if (anovaType === 'repeatedMeasures') {
    const groupCol = mapping.groupColumn ?? mapping.factorAColumn;
    const valueCol = mapping.valueColumn;
    const subjectCol = mapping.subjectColumn;
    const repeatedMethod = options.anovaRepeatedMethod ?? 'repeated';
    if (!groupCol || !valueCol || !subjectCol) {
      throw new KeisukeAdapterError('groupValueSubjectRequired');
    }
    const data = gridData
      .map((row) => {
        const num = parseFiniteNumberFromCell(row[valueCol]);
        if (num === null) return null;
        const subject = String(row[subjectCol] ?? '').trim();
        const group = String(row[groupCol] ?? '').trim();
        if (!subject || !group) return null;
        return { subject, group, value: num };
      })
      .filter(
        (r): r is { subject: string; group: string; value: number } => r !== null
      );
    if (data.length === 0) throw new KeisukeAdapterError('invalidNumericData');

    if (repeatedMethod === 'lmm') {
      const subjectGroups = new Map<string, Set<string>>();
      for (const row of data) {
        if (!subjectGroups.has(row.subject)) subjectGroups.set(row.subject, new Set());
        subjectGroups.get(row.subject)!.add(row.group);
      }
      const hasRepeated = Array.from(subjectGroups.values()).some((levels) => levels.size > 1);
      if (!hasRepeated) {
        throw new KeisukeAdapterError('repeatedLmmSubjectLevelsRequired');
      }
      const result = lmmOneWayANOVA(data);
      const effectivePostHoc = resolveOneWayPostHoc(
        result.groups.length,
        options.postHoc as string | undefined
      );
      return oneWayResultToCache(
        result,
        effectivePostHoc,
        true,
        'lmm',
        options.anovaControlGroup as string | undefined
      );
    }

    const result = repeatedMeasuresOneWayANOVA(data);
    const effectivePostHoc = resolveOneWayPostHoc(result.groups.length, options.postHoc as string | undefined);
    return oneWayResultToCache(
      result,
      effectivePostHoc,
      true,
      'repeated',
      options.anovaControlGroup as string | undefined
    );
  }

  throw new KeisukeAdapterError('unsupportedAnovaType', { type: anovaType });
}
